import { useState } from "react";
import {
  Box, Paper, Stack, Button, Chip, MenuItem, TextField, Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { salesInvoicesApi, type SalesInvoiceListDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { PageHeader } from "@/components/PageHeader";
import { StatusPill } from "@/components/StatusPill";
import { formatDate } from "@/shared/datetime";

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });
const STATUS = ["", "Draft", "Submitted", "Approved", "Posted", "Cancelled"];

export function SalesInvoicesPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("");
  const [billType, setBillType] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const canCreate = useAuth(s => s.hasPermission("sales.invoice.create"));

  const query = useQuery({
    queryKey: ["sales-invoices", status, billType, pagination.page, pagination.pageSize],
    queryFn: () => salesInvoicesApi.list({
      status: status || undefined, billType: billType || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const columns: GridColDef<SalesInvoiceListDto>[] = [
    { field: "documentNo", headerName: "Invoice #", width: 140 },
    { field: "documentDate", headerName: "Date", width: 110,
      valueFormatter: (v) => v ? formatDate(v as string) : "" },
    { field: "billType", headerName: "Type", width: 110,
      renderCell: (p) => <Chip size="small" label={p.value} variant="outlined" />,
    },
    { field: "customerName", headerName: "Customer", flex: 1, minWidth: 180 },
    { field: "customerPhone", headerName: "Phone", width: 130 },
    {
      field: "status", headerName: "Status", width: 120,
      renderCell: (p) => <StatusPill label={p.value as string} />,
    },
    {
      field: "netAmount", headerName: "Net", width: 130, type: "number",
      valueFormatter: (v) => inr.format(Number(v ?? 0)),
    },
    {
      field: "balanceDue", headerName: "Balance", width: 130, type: "number",
      valueFormatter: (v) => inr.format(Number(v ?? 0)),
      cellClassName: (p) => Number(p.value) > 0 ? "balance-due" : "",
    },
  ];

  return (
    <Box>
      <PageHeader
        breadcrumbs={["Sales", "Invoices"]}
        title="Sales invoices"
        subtitle="Outpatient & inpatient bills."
        actions={
          canCreate ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate("/sales/invoices/new")}>
              New OP bill
            </Button>
          ) : undefined
        }
      />
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}
            size="small" sx={{ minWidth: 160 }}>
            {STATUS.map((s) => <MenuItem key={s} value={s}>{s || "All"}</MenuItem>)}
          </TextField>
          <TextField select label="Bill type" value={billType} onChange={(e) => setBillType(e.target.value)}
            size="small" sx={{ minWidth: 160 }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="OutPatient">Outpatient</MenuItem>
            <MenuItem value="InPatient">Inpatient</MenuItem>
          </TextField>
        </Stack>
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        <Box sx={{ height: 600, "& .balance-due": { color: "error.main", fontWeight: 600 } }}>
          <DataGrid
            rows={query.data?.items ?? []}
            columns={columns}
            loading={query.isLoading}
            paginationMode="server"
            rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowClick={(p) => navigate(`/sales/invoices/${p.row.id}`)}
            sx={{ cursor: "pointer" }}
          />
        </Box>
      </Paper>
    </Box>
  );
}
